import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { RoleGuideBanner } from '../components/RoleGuideBanner';
import PropertyCard from '../components/PropertyCard';
import DealTracker from '../components/DealTracker';
import BuyerConsultations from '../components/BuyerConsultations';
import FindProfessionals from '../components/FindProfessionals';
import DashboardTabs from '../components/DashboardTabs';
import BuyerJourney from '../components/BuyerJourney';
import GovtLinksPanel from '../components/GovtLinksPanel';
import MarketTrends from '../components/MarketTrends';
import InviteTeaserCard from '../components/InviteTeaserCard';
import FindProfessionalPromo from '../components/FindProfessionalPromo';
import RecoveryDiscountBanner from '../components/RecoveryDiscountBanner';
import AffiliateWidget from '../components/AffiliateWidget';
import LocalProsPanel from '../components/LocalProsPanel';
import { useQueryTab } from '../hooks/useQueryTab';
import { useCountry } from '../contexts/CountryContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Home, FileText, MessageSquare, ClipboardCheck, Briefcase,
  Calendar as CalendarIcon, MapPin, Clock, Shield, FileCheck,
  AlertCircle, CheckCircle2, Upload, HelpCircle, Key, Users,
  Search as SearchIcon, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TENANT_TABS = [
  { id: 'rentals', label: 'My Saved Rentals', shortLabel: 'Rentals', icon: <Home className="h-4 w-4" />, testId: 'tab-rentals' },
  { id: 'tenancies', label: 'My Tenancy Deals', shortLabel: 'Tenancies', icon: <FileText className="h-4 w-4" />, testId: 'tab-tenancies' },
  { id: 'messages', label: 'Messages & Advice', shortLabel: 'Messages', icon: <MessageSquare className="h-4 w-4" />, testId: 'tab-messages' },
  { id: 'guide', label: 'Renting Guide', shortLabel: 'Guide', icon: <ClipboardCheck className="h-4 w-4" />, testId: 'tab-guide' },
  { id: 'find-agent', label: 'Find a Letting Agent', shortLabel: 'Agents', icon: <Briefcase className="h-4 w-4" />, testId: 'tab-find-agent' },
];

const TENANT_FAQ = [
  { q: 'How does Shopperden help tenants?', a: 'Shopperden connects you with verified landlords. Browse rental listings, book viewings, submit applications, and track your tenancy — all in one place.' },
  { q: 'Is there a fee for tenants?', a: 'In the UK, tenant fees are capped under the Tenant Fees Act 2019. Check the fees page for your country. Some markets offer free tenant access.' },
  { q: 'What happens after I find a rental?', a: 'Once matched with a landlord, your Tenancy Deal tracker guides you through: Application → References → Agreement Signed → Deposit Protected → Keys Collected.' },
  { q: 'How is my deposit protected?', a: 'In the UK, your landlord must protect your deposit in a government scheme (DPS, MyDeposits, or TDS) within 30 days. The deal tracker reminds them.' },
  { q: 'Can I see the full address before paying?', a: 'Full addresses are revealed after identity verification to protect landlords from time-wasters. You can see the area, photos, and 360° tours before verifying.' },
  { q: 'What is Right to Rent?', a: 'UK landlords must verify your immigration status before letting. Shopperden\'s ID verification helps satisfy this requirement for both parties.' },
];

const VerificationBadge = ({ verification, onVerify }) => {
  if (!verification || verification.status === 'not_submitted') {
    return (
      <button onClick={onVerify} className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 text-sm font-medium hover:bg-amber-100 transition-colors" data-testid="verify-id-btn">
        <AlertCircle className="h-4 w-4" /> Verify Your ID
      </button>
    );
  }
  if (verification.status === 'verified') {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 text-sm font-medium" data-testid="id-verified-badge">
        <CheckCircle2 className="h-4 w-4" /> ID Verified
      </div>
    );
  }
  if (verification.status === 'deferred') {
    return (
      <button onClick={onVerify} className="flex items-center gap-2 px-4 py-2 bg-slate-50 text-slate-600 text-sm font-medium hover:bg-slate-100 transition-colors" data-testid="complete-verification-btn">
        <Upload className="h-4 w-4" /> Complete Verification
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 text-[#E07A3A] text-sm font-medium">
      <Clock className="h-4 w-4" /> Verification Pending
    </div>
  );
};

const TenantDashboard = () => {
  const { country } = useCountry();
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [activeTab, setActiveTab] = useQueryTab('rentals', ['rentals', 'tenancies', 'messages', 'guide', 'find-agent', 'govt-links', 'consultations']);
  const [verifyForm, setVerifyForm] = useState({ id_type: '', id_number: '', provide_later: false });
  const [verifyLoading, setVerifyLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [bookingsRes, favoritesRes, verificationRes] = await Promise.all([
        axios.get(`${API_URL}/api/bookings/my-bookings`),
        axios.get(`${API_URL}/api/favorites`),
        axios.get(`${API_URL}/api/verification/status`)
      ]);
      setBookings(bookingsRes.data);
      setFavorites(favoritesRes.data);
      setVerification(verificationRes.data);
    } catch (error) {
      logger.error('Error fetching tenant data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleVerifySubmit = async () => {
    if (!verifyForm.id_type || (!verifyForm.id_number && !verifyForm.provide_later)) {
      toast.error('Please fill in the required fields');
      return;
    }
    setVerifyLoading(true);
    try {
      await axios.post(`${API_URL}/api/verification/submit`, verifyForm);
      toast.success(verifyForm.provide_later ? 'ID verification deferred' : 'ID submitted for verification');
      setShowVerifyModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to submit verification');
    } finally {
      setVerifyLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-50 text-green-700';
      case 'pending': return 'bg-amber-50 text-amber-700';
      case 'cancelled': return 'bg-red-50 text-red-700';
      default: return 'bg-slate-50 text-slate-600';
    }
  };

  const confirmedViewings = bookings.filter(b => b.status === 'confirmed').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="tenant-dashboard">
      <Header />
      <RoleGuideBanner currentSection="/tenant" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* CLEAN HEADER — rental search first. Pros/agents/affiliate
            promos relocated to the collapsible "Tools & Offers" drawer
            at the bottom (matches Buyer/Seller/Agent simplification). */}

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
          <div>
            <span className="text-xs tracking-[0.3em] text-[#E07A3A] mb-1 block uppercase font-medium">Tenant Portal</span>
            <h1 className="font-['Playfair_Display'] text-2xl sm:text-4xl text-[#1A1A1A]" data-testid="tenant-dashboard-title">My Rental Search</h1>
            <p className="text-sm text-slate-500 mt-1">
              Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}. Search rentals direct from verified landlords.
            </p>
          </div>
          {/* Verification Badge */}
          <VerificationBadge verification={verification} onVerify={() => setShowVerifyModal(true)} />
        </div>

        {/* PRIMARY CTA — Browse Rentals. Tenants land here to search;
            this button is the first action they should be able to take. */}
        <Link to="/properties?listing_type=rent" data-testid="tenant-browse-rentals-hero">
          <div className="mt-5 mb-6 flex flex-wrap items-center gap-4 bg-gradient-to-r from-[#FEF8EC] via-white to-[#FEF3EC] text-[#0F172A] px-5 py-4 border border-[#E07A3A]/40 hover:border-[#E07A3A] transition-colors group rounded">
            <div className="w-10 h-10 bg-[#E07A3A]/10 border border-[#E07A3A]/30 flex items-center justify-center flex-shrink-0">
              <SearchIcon className="h-5 w-5 text-[#E07A3A]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-[#E07A3A] tracking-[0.2em] uppercase font-bold mb-0.5">Search property</p>
              <p className="text-sm font-semibold text-[#0F172A] truncate">
                Browse rentals across UK · India · Canada — direct from landlords
              </p>
            </div>
            <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-white bg-[#E07A3A] px-3 py-1.5 group-hover:bg-[#C4652E] transition-colors flex-shrink-0">
              Browse Rentals →
            </span>
          </div>
        </Link>

        {/* Tabs */}
        <DashboardTabs activeTab={activeTab} onTabChange={setActiveTab} tabs={TENANT_TABS} />

        {/* 50%-off return-customer banner — server-side discount on next checkout. */}
        <RecoveryDiscountBanner dataTestId="tenant-recovery-banner" />

        {/* ═══════ TAB: My Saved Rentals ═══════ */}
        {activeTab === 'rentals' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              <div className="luxury-card p-6 text-center" data-testid="stat-shortlisted">
                <p className="text-3xl font-['Playfair_Display'] text-[#E07A3A]">{favorites.length}</p>
                <p className="text-slate-500 text-sm mt-1">Shortlisted Rentals</p>
              </div>
              <div className="luxury-card p-6 text-center" data-testid="stat-viewings">
                <p className="text-3xl font-['Playfair_Display'] text-[#E07A3A]">{bookings.length}</p>
                <p className="text-slate-500 text-sm mt-1">Viewings Booked</p>
              </div>
              <div className="luxury-card p-6 text-center" data-testid="stat-confirmed">
                <p className="text-3xl font-['Playfair_Display'] text-[#E07A3A]">{confirmedViewings}</p>
                <p className="text-slate-500 text-sm mt-1">Viewings Confirmed</p>
              </div>
              <div className="luxury-card p-6 text-center" data-testid="stat-verification">
                <p className="text-3xl font-['Playfair_Display'] text-[#E07A3A]">
                  {verification?.status === 'verified' ? '1' : '0'}
                </p>
                <p className="text-slate-500 text-sm mt-1">ID Verified</p>
              </div>
            </div>

            {/* PRIMARY: Saved Rentals — what the tab actually promises */}
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">Saved Rentals</h2>
                <Link to="/properties?listing_type=rent">
                  <Button variant="outline" className="text-xs uppercase tracking-wider" data-testid="browse-more-rentals-btn">
                    Browse all
                  </Button>
                </Link>
              </div>
              {favorites.length > 0 ? (
                <div className="property-grid">
                  {favorites.map((property) => (
                    <PropertyCard key={property.id} property={property} />
                  ))}
                </div>
              ) : (
                <div className="luxury-card p-10 text-center" data-testid="empty-saved-rentals">
                  <Shield className="h-12 w-12 text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-500 mb-2">No saved rentals yet</p>
                  <p className="text-slate-400 text-xs mb-4">Browse properties and tap the heart icon to save</p>
                  <Link to="/properties?listing_type=rent">
                    <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E]" data-testid="empty-browse-rentals-btn">
                      Browse Rentals
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* SECONDARY: Upcoming Viewings */}
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6">Upcoming Viewings</h2>
              {bookings.length > 0 ? (
                <div className="space-y-4">
                  {bookings.map((booking) => (
                    <div key={booking.id} className="luxury-card p-5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-testid={`viewing-${booking.id}`}>
                      <div className="flex-1">
                        <h3 className="text-[#1A1A1A] font-medium text-lg">{booking.property_title || 'Rental Property'}</h3>
                        <div className="flex flex-wrap gap-4 mt-2 text-slate-500 text-sm">
                          <span className="flex items-center gap-1"><CalendarIcon className="h-4 w-4" />{booking.date}</span>
                          <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{booking.time}</span>
                          {booking.property_address && (
                            <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{booking.property_address}</span>
                          )}
                        </div>
                      </div>
                      <span className={`px-4 py-1.5 text-xs tracking-wider uppercase font-medium ${getStatusColor(booking.status)}`}>
                        {booking.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="luxury-card p-10 text-center">
                  <CalendarIcon className="h-12 w-12 text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-500">No viewings booked yet</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* ═══════ TAB: My Tenancy Deals ═══════ */}
        {activeTab === 'tenancies' && (
          <div className="space-y-6">
            <div className="luxury-card p-5 bg-violet-50/50 border-violet-100 mb-4" data-testid="tenancy-info-banner">
              <div className="flex items-start gap-3">
                <Key className="h-5 w-5 text-violet-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-violet-800">Your Tenancy Milestones</p>
                  <p className="text-xs text-violet-600 mt-0.5 leading-relaxed">
                    Rental deals use tenant-specific steps: Enquiry, Viewing, Application, References, Agreement, Deposit Protected, Keys Collected.
                    Each step keeps you and your landlord aligned throughout the process.
                  </p>
                </div>
              </div>
            </div>
            <DealTracker userRole="tenant" />
            <div className="luxury-card p-6">
              <h3 className="font-['Playfair_Display'] text-lg text-[#1A1A1A] mb-4">Rental Market Trends</h3>
              <MarketTrends />
            </div>
          </div>
        )}

        {/* ═══════ TAB: Messages & Advice ═══════ */}
        {activeTab === 'messages' && <BuyerConsultations />}

        {/* ═══════ TAB: Renting Guide ═══════ */}
        {activeTab === 'guide' && <BuyerJourney userRole="tenant" />}

        {/* ═══════ TAB: Find a Letting Agent ═══════ */}
        {activeTab === 'find-agent' && <FindProfessionals userRole="buyer" />}

        {/* Government Resources */}
        {activeTab === 'govt-links' && <GovtLinksPanel country={country || 'UK'} />}

        {/* Help & Getting Started — collapsible at bottom */}
        <details className="mt-8" data-testid="tenant-help-guide">
          <summary className="cursor-pointer flex items-center gap-2 text-sm text-[#E07A3A] font-semibold tracking-wider uppercase hover:text-[#C4652E] transition-colors py-3">
            <HelpCircle className="h-4 w-4" />
            Help & Getting Started Guide
          </summary>
          <div className="mt-3 space-y-4">
            <div className="luxury-card p-6">
              <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2">Renting with Shopperden</h3>
              <p className="text-sm text-slate-500 mb-6">Everything you need to know about finding your next rental home.</p>
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {[
                  { title: 'Browse Verified Rentals', desc: 'Search listings with 360° tours, AI insights, and verified landlords. Filter by location, price, and bedrooms.' },
                  { title: 'Get Verified', desc: 'Submit your ID for verification. Once approved, landlords see you as a trusted tenant — and you can see full property addresses.' },
                  { title: 'Apply & Track', desc: 'Submit applications, pass referencing, and track every step of your tenancy deal — from viewing to keys collected.' },
                  { title: 'Deposit Protection', desc: 'Your deposit must be protected in a government scheme. The deal tracker ensures your landlord completes this within 30 days.' },
                ].map(step => (
                  <div key={step.title} className="p-4 bg-slate-50 border border-slate-100">
                    <h4 className="text-sm font-semibold text-[#1A1A1A] mb-1">{step.title}</h4>
                    <p className="text-xs text-slate-500 leading-relaxed">{step.desc}</p>
                  </div>
                ))}
              </div>
              <h4 className="text-sm font-semibold text-[#1A1A1A] mb-3 uppercase tracking-wider">Common Questions</h4>
              <div className="space-y-3">
                {TENANT_FAQ.map(item => (
                  <details key={item.q} className="group">
                    <summary className="cursor-pointer text-sm text-[#1A1A1A] font-medium hover:text-[#E07A3A] transition-colors flex items-center gap-2">
                      <HelpCircle className="h-3.5 w-3.5 text-slate-400 group-open:text-[#E07A3A] transition-colors flex-shrink-0" />
                      {item.q}
                    </summary>
                    <p className="text-xs text-slate-500 leading-relaxed mt-1 ml-5.5 pl-1">{item.a}</p>
                  </details>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-6 pt-4 border-t border-slate-100">
                Shopperden is an introduction service. Always verify property details and landlord credentials independently. Engage qualified professionals before signing any tenancy agreement.
              </p>
            </div>
          </div>
        </details>

        {/* ─── COLLAPSIBLE "TOOLS & OFFERS" — moved here from the top so a
              tenant sees their rental search first, not a wall of pros.
              Default open so nothing is hidden, just relocated. ─── */}
        <details
          className="mt-10 border border-slate-200 rounded-lg bg-white overflow-hidden"
          open
          data-testid="tenant-tools-offers"
        >
          <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between bg-gradient-to-r from-[#FEF8EC] via-white to-[#FEF3EC] border-b border-slate-100 hover:bg-amber-50/40 transition-colors">
            <div className="flex items-center gap-3">
              <Activity className="h-5 w-5 text-[#E07A3A]" />
              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E07A3A]">Tools &amp; Offers</p>
                <p className="text-sm font-semibold text-[#0F172A]">Letting solicitors, local pros &amp; earnings · click to collapse</p>
              </div>
            </div>
            <span className="text-[10px] text-slate-500 hidden sm:block">All optional</span>
          </summary>

          <div className="p-5 space-y-5">
            <FindProfessionalPromo compact country={user?.country || 'UK'} dataTestId="tenant-find-pro-promo" />

            <LocalProsPanel
              role="tenant"
              city={user?.city || ''}
              country={(user?.country || 'UK').toUpperCase()}
              dataTestId="tenant-local-pros-panel"
            />

            <InviteTeaserCard />
            <AffiliateWidget dataTestId="tenant-affiliate-widget" />
          </div>
        </details>
      </div>

      {/* ID Verification Modal */}
      <Dialog open={showVerifyModal} onOpenChange={setShowVerifyModal}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A1A1A]">
              <FileCheck className="h-5 w-5 text-[#E07A3A]" /> ID Verification
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Submit one valid ID for verification. This supports Right to Rent checks and protects both you and the landlord.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-700 font-medium">ID Type</Label>
              <Select value={verifyForm.id_type} onValueChange={(v) => setVerifyForm({ ...verifyForm, id_type: v })}>
                <SelectTrigger className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12" data-testid="id-type-select">
                  <SelectValue placeholder="Select ID Type" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="passport">Passport</SelectItem>
                  <SelectItem value="driving_license">Driving Licence</SelectItem>
                  <SelectItem value="national_id">National ID Card</SelectItem>
                  <SelectItem value="residence_permit">Residence Permit</SelectItem>
                  <SelectItem value="biometric_card">Biometric Residence Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700 font-medium">ID Number</Label>
              <Input value={verifyForm.id_number} onChange={(e) => setVerifyForm({ ...verifyForm, id_number: e.target.value })}
                placeholder="Enter your ID number" className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12"
                disabled={verifyForm.provide_later} data-testid="id-number-input" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="provide-later-tenant" checked={verifyForm.provide_later}
                onChange={(e) => setVerifyForm({ ...verifyForm, provide_later: e.target.checked })}
                className="rounded border-slate-300" data-testid="provide-later-checkbox" />
              <Label htmlFor="provide-later-tenant" className="text-slate-600 text-sm cursor-pointer">
                I'll provide ID later at the tenancy agreement stage
              </Label>
            </div>
            <Button onClick={handleVerifySubmit} disabled={verifyLoading}
              className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] h-12" data-testid="submit-verification-btn">
              {verifyLoading ? 'Submitting...' : verifyForm.provide_later ? 'Submit (Deferred)' : 'Submit for Verification'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantDashboard;
